import { useGoToScanProductInContainer } from '../scan-product-in-container/ScanProductInContainer.navigator'
import { useGoToInventoryProductFix } from '../product-fix/InventoryProductFix.navigator'
import { useGoToScanContainerLocation } from '../scan-container-location/ScanContainerLocation.navigator'
import { ScannedContainer, ScannedLocation } from 'client/scanner/scanner.types'
import { useEffect, useState } from 'react'
import { getProducts } from 'client/inventory/inventory.client'
import { mapProductsWithInfo } from 'client/products/products.client'
import { useAtom } from 'jotai'
import { inventoryFixOriginInfoReader, inventoryFixSelectedProductAtom, inventoryProductsAtom } from '../InventoryFix.state'
import { InventoryProduct } from 'types/model'
import { toast } from 'react-toastify'
import { useScanner } from '../../../hooks/scanner.hook'
import { getProductFromScan } from 'client/scanner/scanner.client'
import { useConfirmationDialog } from 'components/molecules/ConfirmationDialog.molecule'

interface InventoryProductListHookResponse {
  origin: ScannedLocation | ScannedContainer
  filteredProducts: InventoryProduct[]
  isLoading: boolean
  searchProductDialogRef: React.RefObject<HTMLDialogElement>
  actions: {
    goToScanProduct: () => void
    back: () => void
    handleFilterChange: (value: string) => void
    setSelectedProduct: (product: InventoryProduct) => void
    openSearchProductDialog: () => void
    searchProduct: (ean: string) => void
  }
}

export const useInventoryProductsList = (): InventoryProductListHookResponse => {
  const scanProduct = useGoToScanProductInContainer()
  const inventoryProductFix = useGoToInventoryProductFix()
  const scanContainerLocation = useGoToScanContainerLocation()
  const { keys, getScan} = useScanner()
  const {
    dialogRef: searchProductDialogRef,
    open: openSearchProductDialog,
  } = useConfirmationDialog()

  const [origin] = useAtom(inventoryFixOriginInfoReader)
  const [products, setProducts] = useAtom(inventoryProductsAtom)
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [, setSelectedProduct] = useAtom(inventoryFixSelectedProductAtom)

  const [isLoading, setIsLoading] = useState(false)

  /* Getting products */
  useEffect(() => {
    setIsLoading(true)
    getProducts(origin.container?.name ?? origin.location.name)
      .then(async (products) => {
        if (products.length === 0) {
          return []
        }
        return await mapProductsWithInfo(products)
      })
      .then((products) => {
        setProducts(products)
        setFilteredProducts(products)
      })
      .catch((err) => {
        toast.error((err as Error).message ?? 'Error getting products')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  const searchProduct = async (ean: string) => {
    getProductFromScan(ean)
    .then((p)=>{
      const inventoryProduct = products.find((product) => product.productEan === p.product.ean)
      if(inventoryProduct) {
        setSelectedProduct(inventoryProduct)
        inventoryProductFix.go({ origin, ean: p.product.ean })
      }
      else {
        toast.error('El producto escaneado no pertenece a la lista de productos')
      }
    })
    .catch(err => {
      toast.error(`ScanContainerLocation.hooks.ts#useEffect ${ean}, error: ${err}`)
    })
  }

  // On scan
  useEffect(() => {
    const scanned = getScan()
    if (scanned) {
      searchProduct(scanned)
    }
  }, [keys])


  const handleFilterChange = (value: string) => {
    if (!value.trim()) {
      setFilteredProducts(products);
    } else {
      const lowercasedValue = value.toLowerCase();
      const filtered = products.filter((product) =>
        product.productEan.toLowerCase().includes(lowercasedValue) ||
        product.productSku.toLowerCase().includes(lowercasedValue) ||
        product.productName.toLowerCase().includes(lowercasedValue)
      );
      setFilteredProducts(filtered);
    }
  };

  return {
    origin,
    filteredProducts,
    isLoading,
    searchProductDialogRef,
    actions: {
      goToScanProduct: () => {
        scanProduct.go({
          origin
        })
      },
      back: () => {
        scanContainerLocation.go()
      },
      handleFilterChange,
      setSelectedProduct,
      openSearchProductDialog,
      searchProduct: (ean: string) => {
        searchProduct(ean)
      }
    }
  }
}
